import { ShieldCheck, ClipboardCheck, FileCheck, Thermometer } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const Quality = () => {
  const { language, isRTL } = useLanguage();

  const qualitySteps = language === 'ar' ? [
    {
      icon: ClipboardCheck,
      title: 'التحقق من المصدر',
      description: 'يتم التحقق من جميع الماشية وتوثيقها من مزارع معتمدة مع سجلات صحية كاملة.'
    },
    {
      icon: Thermometer,
      title: 'التحكم في درجة الحرارة',
      description: 'مراقبة صارمة لدرجة الحرارة طوال المعالجة والتخزين للحفاظ على النضارة.'
    },
    {
      icon: ShieldCheck,
      title: 'فحص الجودة',
      description: 'نقاط فحص جودة متعددة تضمن وصول أفضل المنتجات فقط لعملائنا.'
    },
    {
      icon: FileCheck,
      title: 'التوثيق',
      description: 'تتبع كامل مع سجلات مفصلة في كل مرحلة من سلسلة التوريد.'
    }
  ] : [
    {
      icon: ClipboardCheck,
      title: 'Source Verification',
      description: 'All livestock is verified and documented from certified farms with complete health records.'
    },
    {
      icon: Thermometer,
      title: 'Temperature Control',
      description: 'Strict temperature monitoring throughout processing and storage to maintain freshness.'
    },
    {
      icon: ShieldCheck,
      title: 'Quality Inspection',
      description: 'Multiple quality checkpoints ensure only the best products reach our customers.'
    },
    {
      icon: FileCheck,
      title: 'Documentation',
      description: 'Complete traceability with detailed records at every stage of the supply chain.'
    }
  ];

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {language === 'ar' ? 'الجودة والسلامة' : 'Quality & Safety'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {language === 'ar'
              ? 'سلامتكم أولويتنا - معايير صارمة في كل خطوة'
              : 'Your safety is our priority - rigorous standards at every step'}
          </p>
        </div>
      </section>

      {/* Food Safety Standards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'معايير سلامة الغذاء' : 'Food Safety Standards'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'في إيليت ميت فارم، سلامة الغذاء ليست مجرد متطلب - إنها التزامنا تجاهكم. نلتزم بأكثر معايير ولوائح سلامة الغذاء الدولية صرامة.'
                  : "At EliteMeat Farm, food safety is not just a requirement—it's our commitment to you. We adhere to the most stringent international food safety standards and regulations."}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'يتم فحص ومراجعة مرافقنا بانتظام لضمان الامتثال لجميع بروتوكولات السلامة. نطبق مبادئ تحليل المخاطر ونقاط التحكم الحرجة (HACCP) في جميع عملياتنا.'
                  : 'Our facilities are regularly inspected and audited to ensure compliance with all safety protocols. We implement HACCP (Hazard Analysis Critical Control Points) principles throughout our operations.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'من المزرعة إلى المنتج النهائي، تتم مراقبة وتوثيق والتحقق من كل خطوة لضمان سلامة وجودة منتجات اللحوم لدينا.'
                  : 'From farm to final product, every step is monitored, documented, and verified to guarantee the safety and quality of our meat products.'}
              </p>
            </div>
            <div className={isRTL ? 'md:order-1' : ''}>
              <img
                src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=800"
                alt={language === 'ar' ? 'مراقبة الجودة' : 'Quality Control'}
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quality Control Process */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'عملية مراقبة الجودة لدينا' : 'Our Quality Control Process'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'ضمان جودة متعدد المستويات في كل مرحلة من مراحل الإنتاج'
                : 'Multi-layered quality assurance at every stage of production'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualitySteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-red-600" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Traceability */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-1' : 'md:order-2'}>
              <img
                src="https://images.unsplash.com/photo-1553531889-e6cf4d692b1b?w=800"
                alt={language === 'ar' ? 'التتبع' : 'Traceability'}
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className={isRTL ? 'md:order-2' : 'md:order-1'}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'تتبع كامل' : 'Complete Traceability'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'يمكن تتبع كل منتج إلى مصدره. نظام التتبع الشامل لدينا يسجل كل تفصيل من المزرعة إلى التسليم، مما يضمن الشفافية الكاملة والمساءلة.'
                  : 'Every product can be traced back to its origin. Our comprehensive tracking system records every detail from farm to delivery, ensuring complete transparency and accountability.'}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'يتم الاحتفاظ بأرقام الدُفعات وتواريخ المعالجة وظروف التخزين وسجلات التوزيع في نظامنا الرقمي، مما يوفر رؤية كاملة لسلسلة التوريد.'
                  : 'Batch numbers, processing dates, storage conditions, and distribution records are all maintained in our digital system, providing full visibility of the supply chain.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'يتيح لنا نظام التتبع هذا الاستجابة السريعة لأي مخاوف والحفاظ على أعلى معايير مراقبة الجودة.'
                  : 'This traceability system allows us to quickly respond to any concerns and maintain the highest standards of quality control.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hygiene Practices */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'النظافة والصرف الصحي' : 'Hygiene & Sanitation'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'تعقيم المرافق' : 'Facility Sanitation'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'تنظيف وتعقيم يومي عميق لجميع مناطق المعالجة والمعدات لمنع التلوث.'
                    : 'Daily deep cleaning and sanitization of all processing areas and equipment to prevent contamination.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'تدريب الموظفين' : 'Staff Training'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'برامج تدريب شاملة تضمن أن جميع الموظفين يتبعون بروتوكولات النظافة والسلامة المناسبة.'
                    : 'Comprehensive training programs ensure all staff follow proper hygiene and safety protocols.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'عمليات تدقيق منتظمة' : 'Regular Audits'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'عمليات تدقيق داخلية وخارجية تتحقق من الامتثال لجميع معايير النظافة والسلامة.'
                    : 'Internal and external audits verify compliance with all hygiene and safety standards.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quality;
